import axios from 'axios';
import toast from 'react-hot-toast';
import { getEnvVar } from '../utils/envConfig';
import {
  DeploymentGroupDto,
  CreateDeploymentGroupDto,
  UpdateDeploymentGroupDto,
  DeploymentGroupWithPipelinesDto,
  PipelineDto,
  PipelineWithRunsDto,
  PipelineRunDto,
  AssignPipelinesToGroupDto
} from '../types/pipelineFlow';
import type {
  ReleaseFlowDto,
  CreateReleaseFlowDto,
  UpdateReleaseFlowDto,
  ReleaseFlowValidationResultDto,
  PaginatedResponse,
  ReleaseFlowNodePipelineLinkDto,
  CreateReleaseFlowNodePipelineLinkDto,
  UpdateReleaseFlowNodePipelineLinkDto
} from '../types/releaseFlow';
import type {
  ReleaseDto,
  CreateReleaseDto,
  UpdateReleaseDto,
  ReleaseActionDto,
  ReleaseNodePipelineRunDto,
  CreateReleaseNodePipelineRunDto,
  ReleasesPaginatedResponse,
  ReleasesQueryParams
} from '../types/release';
import type { ReleaseNotesResult, AiReleaseNotesSummary } from '../types/releaseNotes';
import type { AiReleaseNotesSummary as AiReleaseNotesSummaryV2 } from '../types/aiReleaseNotes';
import type {
  CreateProductStackDto,
  ProductStackDto,
  ProductStackOperationAcceptedDto,
  ProductStackOperationDto,
} from '../types/productStack';

const API_BASE_URL = getEnvVar('VITE_API_BASE_URL');

// Create axios instance
const axiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// This will be set by the auth hook
let accessTokenProvider: (() => Promise<string>) | null = null;

// Function to set the token provider (called from auth hook)
export const setTokenProvider = (provider: () => Promise<string>) => {
  accessTokenProvider = provider;
};

// Add request interceptor to include auth token
axiosInstance.interceptors.request.use(
  async (config) => {
    if (accessTokenProvider) {
      try {
        const token = await accessTokenProvider();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to acquire access token:', error);
        throw error;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token might be expired, could trigger re-authentication
      console.error('Unauthorized access - token may be expired');
    }

    if (error.response?.status === 403) {
      toast.error('You do not have permission to perform this action.');
    }
    
    // Extract user-friendly error message from standardized API error response
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // If it's our standardized error response format
      if (errorData.message && errorData.errorCode) {
        // Create a user-friendly error with the server-provided message
        const userError = new Error(errorData.message);
        userError.name = errorData.errorCode;
        (userError as any).statusCode = errorData.statusCode;
        (userError as any).details = errorData.details;
        (userError as any).requestId = errorData.requestId;
        (userError as any).context = errorData.context;
        
        console.error('API Error:', {
          message: errorData.message,
          errorCode: errorData.errorCode,
          statusCode: errorData.statusCode,
          requestId: errorData.requestId,
          details: errorData.details
        });
        
        return Promise.reject(userError);
      }
    }
    
    return Promise.reject(error);
  }
);

const apiClient = {
  // Deployment Groups
  async getDeploymentGroups(): Promise<DeploymentGroupDto[]> {
    const response = await axiosInstance.get('/DeploymentGroups');
    return response.data;
  },

  async getDeploymentGroup(id: string): Promise<DeploymentGroupWithPipelinesDto> {
    const response = await axiosInstance.get(`/DeploymentGroups/${id}`);
    return response.data;
  },

  async createDeploymentGroup(group: CreateDeploymentGroupDto): Promise<DeploymentGroupDto> {
    const response = await axiosInstance.post('/DeploymentGroups', group);
    return response.data;
  },

  async updateDeploymentGroup(id: string, group: UpdateDeploymentGroupDto): Promise<DeploymentGroupDto> {
    const response = await axiosInstance.put(`/DeploymentGroups/${id}`, group);
    return response.data;
  },

  async deleteDeploymentGroup(id: string): Promise<void> {
    await axiosInstance.delete(`/DeploymentGroups/${id}`);
  },

  // Pipelines (entity)
  async getPipeline(id: string, limit: number = 20): Promise<PipelineWithRunsDto> {
    const response = await axiosInstance.get(`/Pipelines/${id}?limit=${limit}`);
    return response.data;
  },

  async deletePipeline(id: string): Promise<void> {
    await axiosInstance.delete(`/Pipelines/${id}`);
  },

  // Pipeline Run Details
  async getPipelineRunDetails(buildId: number): Promise<import('../types/pipelineFlow').PipelineRunDetailsDto> {
    const response = await axiosInstance.get(`/Pipelines/runs/${buildId}/details`);
    return response.data;
  },

  // Pipeline Stages
  async getPipelineStages(buildId: number): Promise<import('../types/pipelineStages').PipelineStagesDto> {
    const response = await axiosInstance.get(`/Pipelines/runs/${buildId}/stages`);
    return response.data;
  },

  // Pipelines (from local DB)
  async getPipelines(deploymentGroupId?: string, includeDeleted?: boolean): Promise<PipelineDto[]> {
    const params = new URLSearchParams();
    if (deploymentGroupId) params.append('deploymentGroupId', deploymentGroupId);
    if (includeDeleted) params.append('includeDeleted', 'true');
    const queryString = params.toString();
    const response = await axiosInstance.get(`/Pipelines${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  async getPipelineRunsByPipelineId(
    pipelineId: string, 
    limit: number = 25, 
    includeStages: boolean = false,
    productionReadyOnly: boolean = false
  ): Promise<PipelineRunDto[]> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (includeStages) params.append('includeStages', 'true');
    if (productionReadyOnly) params.append('productionReadyOnly', 'true');
    const response = await axiosInstance.get(`/Pipelines/${pipelineId}/runs?${params.toString()}`);
    return response.data;
  },

  async assignPipelinesToGroup(dto: AssignPipelinesToGroupDto): Promise<PipelineDto[]> {
    const response = await axiosInstance.post('/Pipelines/assign', dto);
    return response.data;
  },

  // Release Flow APIs
  async getReleaseFlows(params?: {
    isActive?: boolean;
    nameFilter?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedResponse<ReleaseFlowDto>> {
    const response = await axiosInstance.get('/ReleaseFlow', { params });
    
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '0'),
      page: parseInt(response.headers['x-page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || '50'),
      hasNextPage: false,
      hasPreviousPage: false,
    };
  },

  async getReleaseFlow(id: string): Promise<ReleaseFlowDto> {
    const response = await axiosInstance.get(`/ReleaseFlow/${id}`);
    return response.data;
  },

  async createReleaseFlow(data: CreateReleaseFlowDto): Promise<ReleaseFlowDto> {
    const response = await axiosInstance.post('/ReleaseFlow', data);
    return response.data;
  },

  async updateReleaseFlow(id: string, data: UpdateReleaseFlowDto): Promise<ReleaseFlowDto> {
    const response = await axiosInstance.put(`/ReleaseFlow/${id}`, data);
    return response.data;
  },

  async deleteReleaseFlow(id: string): Promise<void> {
    await axiosInstance.delete(`/ReleaseFlow/${id}`);
  },

  async validateReleaseFlow(id: string): Promise<ReleaseFlowValidationResultDto> {
    const response = await axiosInstance.post(`/ReleaseFlow/${id}/validate`);
    return response.data;
  },

  async validateReleaseFlowDto(data: CreateReleaseFlowDto): Promise<ReleaseFlowValidationResultDto> {
    const response = await axiosInstance.post('/ReleaseFlow/validate', data);
    return response.data;
  },

  // ReleaseFlow Node Pipeline Association methods
  async associateNodePipelineLink(
    releaseFlowId: string,
    nodeId: string,
    data: CreateReleaseFlowNodePipelineLinkDto
  ): Promise<ReleaseFlowNodePipelineLinkDto> {
    const response = await axiosInstance.post(`/ReleaseFlow/${releaseFlowId}/nodes/${nodeId}/pipeline`, data);
    return response.data;
  },

  async updateNodePipelineLink(
    releaseFlowId: string, 
    nodeId: string, 
    data: UpdateReleaseFlowNodePipelineLinkDto
  ): Promise<ReleaseFlowNodePipelineLinkDto> {
    const response = await axiosInstance.put(`/ReleaseFlow/${releaseFlowId}/nodes/${nodeId}/pipeline`, data);
    return response.data;
  },

  async removeNodePipelineLink(
    releaseFlowId: string,
    nodeId: string
  ): Promise<void> {
    await axiosInstance.delete(`/ReleaseFlow/${releaseFlowId}/nodes/${nodeId}/pipeline`);
  },

  // Release Management methods
  async getReleases(params?: ReleasesQueryParams): Promise<ReleasesPaginatedResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.nameFilter) queryParams.append('nameFilter', params.nameFilter);
    if (params?.lifecycleState) queryParams.append('lifecycleState', params.lifecycleState);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.releaseFlowId) queryParams.append('releaseFlowId', params.releaseFlowId);
    if (params?.createdBy) queryParams.append('createdBy', params.createdBy);
    
    const response = await axiosInstance.get(`/Release?${queryParams.toString()}`);
    
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '0'),
      page: parseInt(response.headers['x-page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || '20'),
      hasNextPage: false,
      hasPreviousPage: false,
    };
  },

  async getRelease(id: string): Promise<ReleaseDto> {
    const response = await axiosInstance.get(`/Release/${id}`);
    return response.data;
  },

  async createRelease(data: CreateReleaseDto): Promise<ReleaseDto> {
    const response = await axiosInstance.post('/Release', data);
    return response.data;
  },

  async updateRelease(id: string, data: UpdateReleaseDto): Promise<ReleaseDto> {
    const response = await axiosInstance.put(`/Release/${id}`, data);
    return response.data;
  },

  async deleteRelease(id: string): Promise<void> {
    await axiosInstance.delete(`/Release/${id}`);
  },

  async activateRelease(id: string, data: ReleaseActionDto): Promise<ReleaseDto> {
    const response = await axiosInstance.post(`/Release/${id}/activate`, data);
    return response.data;
  },

  async archiveRelease(id: string, data: ReleaseActionDto): Promise<ReleaseDto> {
    const response = await axiosInstance.post(`/Release/${id}/archive`, data);
    return response.data;
  },

  async markReleaseReady(id: string, data: ReleaseActionDto): Promise<ReleaseDto> {
    const response = await axiosInstance.post(`/Release/${id}/ready`, data);
    return response.data;
  },

  // Product Stacks methods
  async getProductStacks(): Promise<ProductStackDto[]> {
    const response = await axiosInstance.get('/ProductStacks');
    return response.data;
  },

  async createProductStack(data: CreateProductStackDto): Promise<ProductStackOperationAcceptedDto> {
    const response = await axiosInstance.post('/ProductStacks', data);
    return response.data;
  },

  async requestDeleteProductStack(productStackId: string): Promise<ProductStackOperationAcceptedDto> {
    const response = await axiosInstance.delete(`/ProductStacks/${productStackId}`);
    return response.data;
  },

  async getProductStackOperations(productStackId: string): Promise<ProductStackOperationDto[]> {
    const response = await axiosInstance.get(`/ProductStacks/${productStackId}/operations`);
    return response.data;
  },

  // Release Pipeline Run Selection methods
  async createPipelineRunSelection(
    releaseId: string,
    data: CreateReleaseNodePipelineRunDto
  ): Promise<ReleaseNodePipelineRunDto> {
    const response = await axiosInstance.post(`/Release/${releaseId}/pipeline-runs`, data);
    return response.data;
  },

  // Release Notes methods
  async getReleaseNotes(releaseId: string): Promise<ReleaseNotesResult> {
    const response = await axiosInstance.get(`/Release/${releaseId}/release-notes`);
    return response.data;
  },

  async generateReleaseNotes(releaseId: string): Promise<ReleaseNotesResult> {
    const response = await axiosInstance.post(`/Release/${releaseId}/release-notes`);
    return response.data;
  },

  async generateReleaseNotesSummary(releaseId: string): Promise<AiReleaseNotesSummary> {
    const response = await axiosInstance.post(`/Release/${releaseId}/release-notes/summary`);
    return response.data;
  },

  // AI Release Notes Summary methods
  async getAiSummary(releaseId: string): Promise<AiReleaseNotesSummaryV2> {
    const response = await axiosInstance.get(`/Release/${releaseId}/ai-summary`);
    return response.data;
  },

  async generateAiSummary(releaseId: string): Promise<AiReleaseNotesSummaryV2> {
    const response = await axiosInstance.post(`/Release/${releaseId}/ai-summary`);
    return response.data;
  },

};

export default apiClient;